import { config } from 'src/config';

export interface LoggerOptions {
  debug: boolean
}

type LogName = 'log' | 'warn' | 'info' | 'error';

type Task = {
  logName: LogName;
  params: any[]
};

/**
 * 系统日志，
 * 关联了Aegis，除了info日志，其他的会同时上报到aegis里面
 * 同时兼容aegis后置，如果aegis还没加载完，延迟上报；
 */

class Logger {
  options: LoggerOptions;
  aegisTask: Task[] = [];
  aegisOptions =  {
    retry: 3,
    retryCount: 0,
    delayTime: 1500,
    delayTimeout: null as any,
  };

  constructor(options: LoggerOptions = { debug: true }) {
    this.options = options;
  }

  /**
   * info 只输出到console
   */
  info(...optionalParams: Task['params']) {
    this.runConsoleLog({
      logName: 'log',
      params: optionalParams,
    });
  }

  /**
   * 同时输出到console 和 aegis
   */
  report(...optionalParams: Task['params']) {
    this.baseLog('info', optionalParams);
  }
  /**
   * 同时输出到console 和 aegis
   */
  warn(...optionalParams: Task['params']) {
    this.runConsoleLog({ logName: 'warn', params: optionalParams });
    this.pushAegisLogInTask({ logName: 'info', params: optionalParams });
  }
  /**
   * 同时输出到console 和 aegis
   */
  error(...optionalParams: Task['params']) {
    this.baseLog('error', optionalParams);
  }

  baseLog(logName: LogName, params: Task['params']) {
    const task = {
      logName,
      params,
    };
    this.runConsoleLog(task);
    this.pushAegisLogInTask(task);
  }

  pushAegisLogInTask(task?: Task) {
    if (task) {
      this.aegisTask.push(task);
    }

    if (this.getAegisInstance()) {
      this.runAegisTask();
      clearTimeout(this.aegisOptions.delayTimeout);
      return;
    }
    if (this.aegisOptions.delayTimeout || this.aegisOptions.retryCount >= this.aegisOptions.retry) return;

    this.aegisOptions.delayTimeout = setTimeout(() => {
      this.aegisOptions.retryCount++;
      this.pushAegisLogInTask();
    }, this.aegisOptions.delayTime);
  }

  runAegisTask = () => {
    const aegisInstance = this.getAegisInstance();
    const { errorTask } = this.aegisTask.reduce((pre, task) => {
      if (aegisInstance[task.logName]) {
        aegisInstance[task.logName](...task.params);
      } else {
        pre.errorTask.push(task);
      }
      return pre;
    }, { errorTask: [] as Task[] });

    this.aegisTask = errorTask;

    if (errorTask.length) {
      console.warn(`[Logger runTask]: ${errorTask.length}个任务失败，请检查`);
    }
  };

  runConsoleLog(task: Task) {
    if (this.options.debug) {
      console[task.logName]('%c logger: %c', 'color: #136915', 'color: #000', ...task.params);
    }
  }

  getAegisInstance() {
    return window.aegis;
  };
}

export const logger = new Logger({
  debug: config.debug.logger,
});

const tryOpenIndexDb = () => {
  try {
    const _window = window as any;
    const indexDb = _window.indexedDB || _window.mozIndexedDB || _window.webkitIndexedDB || _window.msIndexedDB;
    if (indexDb) {
      _window.indexedDB.open('MyTestDatabase');
      return true;
    }
    logger.report('indexedDB not found', _window.indexedDB);
  } catch (e) {
    logger.report('indexedDB open error', e);
    return false;
  }
};

tryOpenIndexDb();
